import * as Yup from "yup";
import { useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import { useNavigate } from "react-router-dom";
// material
import {
  Stack,
  Alert,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import Axios from "../../../axios";

// component
import Iconify from "../../../components/Iconify";

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const RegisterSchema = Yup.object().shape({
    firstname: Yup.string()
      .min(3, "Too short!")
      .max(50, "Too long!")
      .required("Firstname is required"),
    lastname: Yup.string()
      .min(3, "Too short!")
      .max(50, "Too long!")
      .required("Lastname is required"),
    username: Yup.string()
      .min(2, "Too short!")
      .max(50, "Too long!")
      .required("Username is required"),
    gender: Yup.string().required("Gender is required"),
    email: Yup.string()
      .email("Must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
    confirmpassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      gender: "",
      email: "",
      username: "",
      password: "",
      confirmpassword: "",
      roles: ["member"],
    },
    validationSchema: RegisterSchema,
    onSubmit: (values, actions) => {
      setError("");
      Axios.post(`/api/auth/signup`, values)
        .then((response) => {
          enqueueSnackbar("User Created Successfully!", { variant: "success" });
          navigate("/login", { replace: true });
          console.log(response);
        })
        .catch((err) => {
          setTimeout(() => {
            setError(err.response.data.message);
            actions.setSubmitting(false);
          }, 1000);
        });
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          )}

          {/* First Name and Last Name Fields */}
          <Stack direction="column" spacing={2}>
            <TextField
              fullWidth
              label="Firstname"
              {...getFieldProps("firstname")}
              error={Boolean(touched.firstname && errors.firstname)}
              helperText={touched.firstname && errors.firstname}
            />
            <TextField
              fullWidth
              label="Lastname"
              {...getFieldProps("lastname")}
              error={Boolean(touched.lastname && errors.lastname)}
              helperText={touched.lastname && errors.lastname}
            />
          </Stack>

          {/* Username Field */}
          <TextField
            fullWidth
            label="Username"
            {...getFieldProps("username")}
            error={Boolean(touched.username && errors.username)}
            helperText={touched.username && errors.username}
          />

          {/* Gender Field */}
          <TextField
            fullWidth
            select
            label="Gender"
            {...getFieldProps("gender")}
            error={Boolean(touched.gender && errors.gender)}
            helperText={touched.gender && errors.gender}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          {/* Email Field */}
          <TextField
            fullWidth
            autoComplete="email"
            type="email"
            label="Email address"
            {...getFieldProps("email")}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          {/* Password Field */}
          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            label="Password"
            {...getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />

          {/* Confirm Password Field */}
          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            label="Confirm Password"
            {...getFieldProps("confirmpassword")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.confirmpassword && errors.confirmpassword)}
            helperText={touched.confirmpassword && errors.confirmpassword}
          />

          {/* Submit Button */}
          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
            Proceed
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
